import { useMemo, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { useLoaderData } from "react-router-dom";
import colors from "../../styles/colors";
import CommonWrapper from "../../components/ui_components/CommonComponentsStructureCss";
import { apiGetBeltById, apiGetBeltByIdTag, apiAddChampionToHallOfFame, apiRemoveChampionFromHallOfFame } from "../../api/beltApi";
import type { AddChampionPayload } from "../../api/beltApi";
import DeleteChampionModale from "../../components/admin/HallOfFameHandleFolder/DeleteChampionModale";
import AddChampionModale from "../../components/admin/HallOfFameHandleFolder/AddChampionModale";
import type { Belt } from "../../types";

interface BeltWrestler {
    id: number;
    wrestler_belt_id?: number;
    historyId?: number;
    name: string;
    avatar_path: string;
    date_win: string;
    ppv_name: string;
    ppv_id: number | null;
    ppv_img_path?: string;
    partner_name?: string;
    partner_avatar_path?: string;
}

interface TagWinner {
    id: number;
    name: string;
    avatar_path: string;
}

interface TagListWinner {
    honor_tag_belt_id?: number;
    date_win: string;
    ppv_name: string;
    ppv_id: number | null;
    ppv_img_path?: string;
    show_if_noppv?: string;
    winners: TagWinner[];
}

interface BeltData extends Belt {
    wrestlers?: BeltWrestler[];
    listWinners?: TagListWinner[];
}

// Normalizza i dati per avere un formato unificato
function normalizeChampions(data: BeltData, isTagTeam: boolean): BeltWrestler[] {
    if (!isTagTeam && data.wrestlers) {
        return data.wrestlers.map(w => ({
            ...w,
            historyId: w.wrestler_belt_id
        }));
    }

    if (isTagTeam && data.listWinners) {
        return data.listWinners.map((reign, index) => {
            const mainWrestler = reign.winners[0];
            const partner = reign.winners[1];
            return {
                id: mainWrestler?.id || index,
                historyId: reign.honor_tag_belt_id,
                name: mainWrestler?.name || "VACANTE",
                avatar_path: mainWrestler?.avatar_path || "",
                date_win: reign.date_win,
                ppv_name: reign.ppv_name || reign.show_if_noppv || "",
                ppv_id: reign.ppv_id,
                ppv_img_path: reign.ppv_img_path,
                partner_name: partner?.name,
                partner_avatar_path: partner?.avatar_path
            };
        });
    }

    return [];
}

const Container = styled.div`
    width: 90%;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding-top: 20px;
    padding-bottom: 40px;

    & h1.admin-hof-handle-title {
        font-size: 24px;
        font-weight: 600;
        text-align: center;
        margin-bottom: 10px;
    }

    & p.admin-hof-handle-description {
        font-size: 16px;
        font-weight: 400;
        text-align: center;
        margin-bottom: 10px;
    }
`;

const BeltsListSection = styled.div`
    width: 100%;
    margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
    font-size: 18px;
    font-weight: 600;
    color: gold;
    margin-bottom: 16px;
    text-align: center;
`;

const BeltsGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: center;
`;

const BeltItem = styled.div<{ $isSelected?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background-color: ${colors.bgColorBlack2};
    border: 2px solid ${props => props.$isSelected ? 'gold' : 'rgba(255, 255, 255, 0.1)'};
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    min-width: 150px;

    &:hover {
        border-color: gold;
        transform: scale(1.02);
    }

    img {
        width: 80px;
        height: 50px;
        object-fit: contain;
    }

    span {
        font-size: 14px;
        font-weight: 500;
        text-align: center;
        color: ${props => props.$isSelected ? 'gold' : 'white'};
    }
`;

const BeltImagePlaceholder = styled.div`
    width: 80px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
    font-size: 10px;
    color: rgba(255, 255, 255, 0.5);
`;

const HallOfFameSection = styled.div`
    width: 100%;
`;

const SelectedBeltHeader = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    padding: 20px;
    background-color: ${colors.bgColorBlack2};
    border: 2px solid gold;
    border-radius: 8px;

    img {
        max-width: 200px;
        max-height: 100px;
        object-fit: contain;
    }

    h2 {
        color: gold;
        font-size: 22px;
        margin: 0;
    }
`;

const ActionButtonsContainer = styled.div`
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
    justify-content: center;
`;

const ActionButton = styled.button`
    padding: 10px 20px;
    border-radius: 5px;
    border: 1px solid white;
    background-color: ${colors.bgColorBlack2};
    color: white;
    font-size: 16px;
    cursor: pointer;
    transition: transform 0.2s ease-in-out, background-color 0.2s ease-in-out;

    &:hover {
        transform: scale(1.05);
        background-color: ${colors.bgColorBlack};
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ChampionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const ChampionRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background-color: ${colors.bgColorBordeaux};
    border: 2px solid ${colors.bgColorBlack2};
    border-radius: 8px;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 12px;
    }
`;

const ChampionInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;

    img {
        width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 50%;
        border: 2px solid white;
    }

    .wrestler-names {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .main-name {
            font-size: 16px;
            font-weight: 600;
            color: white;
        }

        .partner-name {
            font-size: 14px;
            color: rgba(255, 255, 255, 0.7);
        }
    }
`;

const ChampionDate = styled.div`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.9);
    text-align: center;
    flex: 1;
`;

const ChampionPpv = styled.div`
    font-size: 14px;
    color: rgba(255, 255, 255, 0.8);
    text-align: center;
    flex: 1;
`;

const RemoveButton = styled.button`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid white;
    background-color: transparent;
    color: white;
    font-size: 20px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s ease-in-out;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: white;
        color: ${colors.bgColorBlack};
    }
`;

const InfoBox = styled.div`
    width: 100%;
    background-color: ${colors.bgColorBlack2};
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 20px;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
`;

const LoadingBox = styled.div`
    width: 100%;
    padding: 40px;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
`;

interface PaginatedResponse {
    status?: string;
    data?: {
        data?: Belt[];
        current_page?: number;
        total?: number;
    } | Belt[];
}

function HallOfFameHandle() {
    const loaderData = useLoaderData() as Belt[] | PaginatedResponse | null;

    const initialBelts = useMemo(() => {
        if (!loaderData) return [];
        if (Array.isArray(loaderData)) return loaderData;
        if (loaderData && typeof loaderData === "object") {
            // Handle paginated response: { status, data: { data: [...] } }
            if ("data" in loaderData && loaderData.data) {
                if (Array.isArray(loaderData.data)) {
                    return loaderData.data;
                }
                if (typeof loaderData.data === "object" && "data" in loaderData.data && Array.isArray(loaderData.data.data)) {
                    return loaderData.data.data;
                }
            }
        }
        return [];
    }, [loaderData]);

    const belts = initialBelts;
    const [selectedBelt, setSelectedBelt] = useState<Belt | null>(null);
    const [selectedBeltData, setSelectedBeltData] = useState<BeltData | null>(null);
    const [isLoadingBeltData, setIsLoadingBeltData] = useState(false);
    const fetchBeltAbortRef = useRef<AbortController | null>(null);
    useEffect(() => { return () => { fetchBeltAbortRef.current?.abort(); }; }, []);

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [championToDelete, setChampionToDelete] = useState<{
        historyId?: number;
        wrestlerName: string;
        wrestlerAvatar?: string;
        dateWin: string;
    } | null>(null);

    const sortedBelts = useMemo(() => {
        return [...belts].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it"));
    }, [belts]);

    const fetchBeltData = async (belt: Belt) => {
        if (fetchBeltAbortRef.current) fetchBeltAbortRef.current.abort();
        fetchBeltAbortRef.current = new AbortController();
        const { signal } = fetchBeltAbortRef.current;
        setIsLoadingBeltData(true);
        try {
            const apiCall = belt.is_team || belt.is_tag ? apiGetBeltByIdTag : apiGetBeltById;
            const response = await apiCall(belt.id) as any;
            if (signal.aborted) return;
            const data = response?.data ?? response;
            setSelectedBeltData(data);
        } catch (error) {
            if (error instanceof Error && error.name === 'AbortError') return;
            console.error("Errore nel caricamento dei dati della cintura:", error);
            setSelectedBeltData(null);
        } finally {
            if (!signal.aborted) setIsLoadingBeltData(false);
        }
    };

    const handleBeltClick = async (belt: Belt) => {
        setSelectedBelt(belt);
        await fetchBeltData(belt);
    };

    const handleAddChampion = async (data: AddChampionPayload) => {
        const response = await apiAddChampionToHallOfFame(data) as any;

        if (response?.status === "error" || response?.error) {
            throw new Error(response?.message || response?.error || "Errore durante l'aggiunta");
        }

        if (selectedBelt) {
            await fetchBeltData(selectedBelt);
        }
    };

    const handleRemoveChampion = async (historyId: number, beltId: number) => {
        const response = await apiRemoveChampionFromHallOfFame(historyId, beltId) as any;

        if (response?.status === "error" || response?.error) {
            throw new Error(response?.message || response?.error || "Errore durante la rimozione");
        }

        if (selectedBelt) {
            await fetchBeltData(selectedBelt);
        }
    };

    const openDeleteModal = (wrestler: BeltWrestler) => {
        setChampionToDelete({
            historyId: wrestler.historyId,
            wrestlerName: wrestler.partner_name ? `${wrestler.name} & ${wrestler.partner_name}` : wrestler.name,
            wrestlerAvatar: wrestler.avatar_path,
            dateWin: wrestler.date_win
        });
        setIsDeleteModalOpen(true);
    };

    return (
        <CommonWrapper className="admin-hof-handle-common-wrapper">
            <Container className="admin-hof-handle-container">
                <h1 className="admin-hof-handle-title">Gestisci Albo d'Oro</h1>
                <p className="admin-hof-handle-description">
                    Seleziona una cintura per visualizzare e gestire il suo albo d'oro.
                </p>

                <BeltsListSection className="admin-hof-belts-list">
                    <SectionTitle>Seleziona Cintura</SectionTitle>
                    {sortedBelts.length === 0 ? (
                        <InfoBox>Nessuna cintura disponibile.</InfoBox>
                    ) : (
                        <BeltsGrid className="admin-hof-belts-grid">
                            {sortedBelts.map((belt) => (
                                <BeltItem
                                    key={belt.id}
                                    $isSelected={selectedBelt?.id === belt.id}
                                    onClick={() => handleBeltClick(belt)}
                                >
                                    {belt.img_path ? (
                                        <img
                                            src={process.env.REACT_APP_STORAGE_URL + belt.img_path}
                                            alt={belt.name}
                                        />
                                    ) : (
                                        <BeltImagePlaceholder>No img</BeltImagePlaceholder>
                                    )}
                                    <span>{belt.name}</span>
                                </BeltItem>
                            ))}
                        </BeltsGrid>
                    )}
                </BeltsListSection>

                {selectedBelt && (
                    <HallOfFameSection className="admin-hof-section">
                        <SelectedBeltHeader className="admin-hof-selected-header">
                            {selectedBelt.img_path && (
                                <img
                                    src={process.env.REACT_APP_STORAGE_URL + selectedBelt.img_path}
                                    alt={selectedBelt.name}
                                />
                            )}
                            <h2>{selectedBelt.name}</h2>
                        </SelectedBeltHeader>

                        <ActionButtonsContainer className="admin-hof-actions">
                            <ActionButton
                                onClick={() => setIsAddModalOpen(true)}
                                disabled={isLoadingBeltData}
                            >
                                + Aggiungi Campione
                            </ActionButton>
                        </ActionButtonsContainer>

                        {(() => {
                            if (isLoadingBeltData) {
                                return <LoadingBox>Caricamento albo d'oro...</LoadingBox>;
                            }

                            const isTagTeam = selectedBelt?.is_team || selectedBelt?.is_tag;
                            const champions = selectedBeltData
                                ? normalizeChampions(selectedBeltData, !!isTagTeam)
                                : [];

                            if (champions.length === 0) {
                                return <InfoBox>Nessun campione nell'albo d'oro di questa cintura.</InfoBox>;
                            }

                            return (
                                <ChampionsList className="admin-hof-champions-list">
                                    {champions.map((wrestler, index) => (
                                        <ChampionRow key={`${wrestler.historyId || wrestler.id}-${index}`}>
                                            <ChampionInfo>
                                                <div className="wrestler-names">
                                                    <span className="main-name">
                                                        {wrestler.name || "VACANTE"}
                                                    </span>
                                                    {wrestler.partner_name && (
                                                        <span className="partner-name">
                                                            & {wrestler.partner_name}
                                                        </span>
                                                    )}
                                                </div>
                                            </ChampionInfo>
                                            <ChampionDate>{wrestler.date_win}</ChampionDate>
                                            <ChampionPpv>{wrestler.ppv_name || "-"}</ChampionPpv>
                                            <RemoveButton
                                                onClick={() => openDeleteModal(wrestler)}
                                                title="Rimuovi campione"
                                            >
                                                ×
                                            </RemoveButton>
                                        </ChampionRow>
                                    ))}
                                </ChampionsList>
                            );
                        })()}
                    </HallOfFameSection>
                )}

                {isAddModalOpen && selectedBelt && (
                    <AddChampionModale
                        belt={selectedBelt}
                        onClose={() => setIsAddModalOpen(false)}
                        onSave={handleAddChampion}
                    />
                )}

                {isDeleteModalOpen && championToDelete && selectedBelt && (
                    <DeleteChampionModale
                        champion={championToDelete}
                        beltId={selectedBelt.id}
                        onClose={() => {
                            setIsDeleteModalOpen(false);
                            setChampionToDelete(null);
                        }}
                        onConfirm={handleRemoveChampion}
                    />
                )}
            </Container>
        </CommonWrapper>
    );
}

export default HallOfFameHandle;
